import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mainNavItems } from '../constants';
import { ProfileIcon, PlusIcon, MenuIcon, CloseIcon } from './Icons';

const Logo: React.FC = () => (
  <NavLink to="/" className="flex items-center gap-2 flex-shrink-0">
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 32C24.8366 32 32 24.8366 32 16C32 7.16344 24.8366 0 16 0C7.16344 0 0 7.16344 0 16C0 24.8366 7.16344 32 16 32Z" fill="#0C7FF2"/>
        <path d="M9.41162 16.3529L13.7058 20.647L22.5881 11.7646" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
    <span className="font-bold text-xl text-gray-800 dark:text-gray-200 hidden sm:inline">Mubo.PRO</span>
  </NavLink>
);


const Header: React.FC = () => {
    const { t } = useTranslation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location]);

    return (
        <header className="sticky top-0 z-30 w-full bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-800">
            <div className="mx-auto max-w-7xl flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8 gap-4">
                <div className="flex items-center gap-8">
                    <Logo />
                    {/* Desktop Navigation */}
                    <nav className="hidden md:flex items-center gap-6">
                        {mainNavItems.map(item => (
                            <NavLink
                                key={item.labelKey}
                                to={item.href}
                                className={({ isActive }) =>
                                    `text-sm font-medium transition-colors hover:text-[var(--primary-color)] ${
                                        isActive ? 'text-[var(--primary-color)]' : 'text-gray-500 dark:text-gray-400'
                                    }`
                                }
                            >
                                {t(item.labelKey)}
                            </NavLink>
                        ))}
                    </nav>
                </div>

                {/* Desktop Actions */}
                <div className="hidden md:flex items-center gap-3">
                    <button className="flex items-center justify-center gap-2 rounded-lg bg-[var(--primary-color)] px-3 py-2 text-sm font-semibold text-white hover:bg-opacity-90 transition-colors">
                       <PlusIcon />
                       <span>{t('actions.addCreator')}</span>
                    </button>
                    <NavLink to="/profile" className="flex items-center justify-center h-9 w-9 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                       <ProfileIcon variant='fill' />
                    </NavLink>
                </div>

                {/* Mobile Menu Button */}
                <div className="flex items-center md:hidden">
                    <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 text-gray-600 dark:text-gray-300">
                        {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
                    </button>
                </div>
            </div>

            {/* Mobile Menu Panel */}
            {isMobileMenuOpen && (
                 <div 
                    className="md:hidden absolute top-16 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 shadow-lg"
                    onClick={() => setIsMobileMenuOpen(false)}
                >
                    <nav className="flex flex-col p-4 gap-1">
                        {mainNavItems.map(item => (
                            <NavLink
                                key={item.labelKey}
                                to={item.href}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 rounded-md p-3 text-base font-medium transition-colors ${
                                        isActive ? 'bg-blue-50 dark:bg-blue-900/50 text-[var(--primary-color)]' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                                    }`
                                }
                            >
                                {item.icon}
                                {t(item.labelKey)}
                            </NavLink>
                        ))}
                         <div className="border-t my-2 border-gray-100 dark:border-gray-800"></div>
                         <NavLink
                            to="/profile"
                            className={({ isActive }) =>
                                `flex items-center gap-3 rounded-md p-3 text-base font-medium transition-colors ${
                                    isActive ? 'bg-blue-50 dark:bg-blue-900/50 text-[var(--primary-color)]' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                                }`
                            }
                        >
                            <ProfileIcon variant='stroke'/>
                            {t('nav.profile')}
                        </NavLink>
                        <button className="w-full mt-4 flex items-center justify-center gap-2 rounded-lg bg-[var(--primary-color)] px-3 py-3 text-sm font-semibold text-white hover:bg-opacity-90 transition-colors">
                           <PlusIcon />
                           <span>{t('actions.addCreator')}</span>
                        </button>
                    </nav>
                </div>
            )}
        </header>
    );
}

export default Header;